import React, { useState, useEffect } from 'react';
import { analyticsService, RedemptionHeatmapData } from '../../../services/analyticsService';
import Icon from '../../../components/AppIcon';

interface RedemptionHeatmapProps {
  title: string;
}

const RedemptionHeatmap: React.FC<RedemptionHeatmapProps> = ({ title }) => {
  const [heatmapData, setHeatmapData] = useState<RedemptionHeatmapData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  useEffect(() => {
    loadHeatmapData();
  }, []);

  const loadHeatmapData = async () => {
    try {
      setLoading(true);
      const data = await analyticsService.getRedemptionHeatmap();
      setHeatmapData(data);
    } catch (err) {
      console.error('Error loading heatmap data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load heatmap data');
    } finally {
      setLoading(false);
    }
  };

  const getHeatmapValue = (hour: number, dayOfWeek: number) => {
    return heatmapData.find(
      item => item.hour === hour && item.dayOfWeek === dayOfWeek
    ) || { hour, dayOfWeek, redemptions: 0, revenue: 0 };
  };

  const maxRedemptions = Math.max(...heatmapData.map(item => item.redemptions), 1);
  
  const getIntensityColor = (redemptions: number) => {
    const intensity = redemptions / maxRedemptions;
    if (intensity === 0) return 'bg-gray-50 border-gray-200';
    if (intensity <= 0.2) return 'bg-blue-100 border-blue-200';
    if (intensity <= 0.4) return 'bg-blue-200 border-blue-300';
    if (intensity <= 0.6) return 'bg-blue-300 border-blue-400';
    if (intensity <= 0.8) return 'bg-blue-400 border-blue-500';
    return 'bg-blue-500 border-blue-600';
  };

  const getIntensityText = (redemptions: number) => {
    const intensity = redemptions / maxRedemptions;
    if (intensity === 0) return 'text-gray-500';
    if (intensity <= 0.4) return 'text-gray-700';
    return 'text-white';
  };

  const formatHour = (hour: number) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  const getPeakHours = () => {
    const hourTotals = hours.map(hour => ({
      hour,
      total: heatmapData
        .filter(item => item.hour === hour)
        .reduce((sum, item) => sum + item.redemptions, 0)
    }));
    
    return hourTotals
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
  };

  const getPeakDays = () => {
    const dayTotals = daysOfWeek.map((day, dayIndex) => ({
      day,
      dayIndex,
      total: heatmapData
        .filter(item => item.dayOfWeek === dayIndex)
        .reduce((sum, item) => sum + item.redemptions, 0)
    }));
    
    return dayTotals
      .sort((a, b) => b.total - a.total)
      .slice(0, 3);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">{title}</h3>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const peakHours = getPeakHours();
  const peakDays = getPeakDays();
  const totalRedemptions = heatmapData.reduce((sum, item) => sum + item.redemptions, 0);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Redemption patterns by hour and day of week (last 30 days)
        </p>
      </div>

      {/* Heatmap Grid */}
      <div className="mb-6 overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Day labels */}
          <div className="flex mb-2">
            <div className="w-12"></div>
            {daysOfWeek.map(day => (
              <div key={day} className="flex-1 text-center text-xs font-medium text-muted-foreground">
                {day}
              </div>
            ))}
          </div>
          
          {/* Heatmap rows */}
          {[0, 4, 8, 12, 16, 20].map(startHour => (
            <div key={startHour} className="flex mb-1">
              <div className="w-12 flex items-center justify-end pr-2">
                <span className="text-xs text-muted-foreground">
                  {formatHour(startHour)}
                </span>
              </div>
              {daysOfWeek.map((_, dayIndex) => {
                const cellData = getHeatmapValue(startHour, dayIndex);
                return (
                  <div
                    key={`${startHour}-${dayIndex}`}
                    className={`flex-1 h-8 m-0.5 rounded border flex items-center justify-center cursor-pointer transition-all duration-200 hover:scale-110 ${getIntensityColor(cellData.redemptions)}`}
                    title={`${daysOfWeek[dayIndex]} ${formatHour(startHour)}: ${cellData.redemptions} redemptions`}
                  >
                    <span className={`text-xs font-medium ${getIntensityText(cellData.redemptions)}`}>
                      {cellData.redemptions > 0 ? cellData.redemptions : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-between mb-6 text-xs">
        <span className="text-muted-foreground">Less activity</span>
        <div className="flex space-x-1">
          {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map((intensity, index) => (
            <div
              key={index}
              className={`w-3 h-3 rounded border ${
                intensity === 0 ? 'bg-gray-50 border-gray-200' :
                intensity <= 0.2 ? 'bg-blue-100 border-blue-200' :
                intensity <= 0.4 ? 'bg-blue-200 border-blue-300' :
                intensity <= 0.6 ? 'bg-blue-300 border-blue-400' :
                intensity <= 0.8 ? 'bg-blue-400 border-blue-500': 'bg-blue-500 border-blue-600'
              }`}
            ></div>
          ))}
        </div>
        <span className="text-muted-foreground">More activity</span>
      </div>

      {/* Insights */}
      <div className="space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">Peak Hours</h4>
          <div className="flex flex-wrap gap-2">
            {peakHours.map((hourData, index) => (
              <span
                key={hourData.hour}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {formatHour(hourData.hour)}: {hourData.total} redemptions
              </span>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-semibold text-foreground mb-2">Peak Days</h4>
          <div className="flex flex-wrap gap-2">
            {peakDays.map((dayData, index) => (
              <span
                key={dayData.dayIndex}
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  index === 0 ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                }`}
              >
                {dayData.day}: {dayData.total} redemptions
              </span>
            ))}
          </div>
        </div>

        <div className="pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Activity (30 days)</span>
            <span className="font-semibold text-foreground">{totalRedemptions.toLocaleString('de-AT')} redemptions</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedemptionHeatmap;